import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import Layouts from "../../layouts/layouts";
import DataTable_Component from "../../components/clientrevenue/Data Table/index";

export default function ClientOrderMatrix() {
  const title = "Client Utilization";

  const [apiData, setApiData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalClients: 0,
  });
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTrips, setTotalTrips] = useState(0);
  const [loadingExport, setLoadingExport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMonth, setExportMonth] = useState(moment().month() + 1);
  const [exportYear, setExportYear] = useState(moment().year());

  const selectedMonth = moment().month() + 1;
  const selectedYear = moment().year();

  /* ✅ SINGLE SOURCE OF TRUTH */
  const fetchData = async (
    page = 1,
    month = selectedMonth,
    year = selectedYear,
    search = "",
    limit = pagination.limit || 10,
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/client-wise/monthly`,
        {
          params: {
            month,
            year,
            page,
            // limit: pagination.limit,
            limit,
            search,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.data?.success) return;

      const clients = response.data.clients || [];
      setApiData(clients);
      setPagination(response.data.pagination || {});
      setTotalRevenue(response.data.totalRevenue || 0);
      setTotalTrips(response.data.totalTrips || 0);

      /* 🔹 COLLECT UNIQUE DATES */
      const dateSet = new Set();
      clients.forEach((client) => {
        client.dateSummary?.forEach((d) => dateSet.add(d.date));
      });

      const sortedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      /* 🔹 STATIC COLUMNS */
      const staticColumns = [
        {
          name: <b>Client</b>,
          selector: (row) => row.clientName,
          sortable: true,
          minWidth: "220px",
        },
        {
          name: <b>Cash Short </b>,
          selector: (row) => row.totalCashShort ?? "-",
          sortable: true,
        },
        {
          name: <b>Total Trips</b>,
          selector: (row) => row.totalTrips,
          center: true,
        },
        {
          name: <b>Total Payout</b>,
          selector: (row) => Number(row.totalPayout || 0).toFixed(2),
          center: true,
        },
      ];

      /* 🔹 DYNAMIC DATE COLUMNS */
      const dynamicColumns = [];

      sortedDates.forEach((date, index) => {
        const headerBg = index % 2 === 0 ? "#eef4ff" : "#f9fafb";

        const headerStyle = {
          backgroundColor: headerBg,
          padding: "6px 4px",
          borderRadius: "4px",
          textAlign: "center",
        };

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <b>{moment(date).format("DD/MM")}</b>
              <div style={{ fontSize: "12px" }}>Trips</div>
            </div>
          ),
          center: true,
          minWidth: "90px",
          selector: (row) => {
            const d = row.dateSummary?.find((x) => x.date === date);
            return d ? d.trips : 0;
          },
        });

        dynamicColumns.push({
          name: (
            <div style={headerStyle}>
              <b>{moment(date).format("DD/MM")}</b>
              <div style={{ fontSize: "12px" }}>Payout</div>
            </div>
          ),
          center: true,
          minWidth: "110px",
          selector: (row) => {
            const d = row.dateSummary?.find((x) => x.date === date);
            return d ? Number(d.payout || 0).toFixed(2) : "0.00";
          },
        });
      });

      setColumns([...staticColumns, ...dynamicColumns]);
    } catch (error) {
      console.error("Client Order Matrix Error:", error);
    }
  };

  /* ✅ INITIAL LOAD (CORRECT ORDER) */
  useEffect(() => {
    fetchData(1);
  }, []);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        console.error("No token found");
        return;
      }

      if (!exportMonth || !exportYear) {
        alert("Please select Month and Year");
        return;
      }

      setLoadingExport(true);

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/orders/client-wise/monthly`,
        {
          params: {
            month: exportMonth,
            year: exportYear,
            page: 1,
            limit: 100000,
            search: "",
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.data?.success) {
        console.error("Export API failed");
        return;
      }

      const clients = response.data.clients || [];

      /*
       * Collect all unique dates from ALL clients
       */
      const dateSet = new Set();

      clients.forEach((client) => {
        client.dateSummary?.forEach((d) => {
          dateSet.add(d.date);
        });
      });

      const sortedDates = Array.from(dateSet).sort(
        (a, b) => new Date(a) - new Date(b),
      );

      /*
       * Convert API data into Excel rows
       */
      const transformedData = clients.map((client, index) => {
        const row = {
          "S.No": index + 1,
          Client: client.clientName || "-",
          "Cash Short": client.totalCashShort ?? 0,
          "Total Trips": client.totalTrips ?? 0,
          "Total Payout": Number(client.totalPayout || 0).toFixed(2),
        };

        /*
         * Add dynamic date columns
         */
        sortedDates.forEach((date) => {
          const d = client.dateSummary?.find((item) => item.date === date);

          row[`${moment(date).format("DD/MM")} Trips`] = d?.trips ?? 0;

          row[`${moment(date).format("DD/MM")} Payout`] = Number(
            d?.payout || 0,
          ).toFixed(2);
        });

        return row;
      });

      /*
       * Create Excel
       */
      const worksheet = XLSX.utils.json_to_sheet(transformedData);

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Client Utilization");

      XLSX.writeFile(
        workbook,
        `client_utilization_${exportYear}_${String(exportMonth).padStart(2, "0")}.xlsx`,
      );

      setShowExportModal(false);
    } catch (error) {
      console.error("Error exporting Client Utilization:", error);
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <>
      <Layouts />

      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              {/* <div className="card-header">
                <h4>{title}</h4>
              </div> */}
              <div className="card-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0">{title}</h4>

                <button
                  className="btn btn-success btn-sm text-white"
                  onClick={() => setShowExportModal(true)}
                >
                  Export to Excel
                </button>
              </div>
              {showExportModal && (
                <div
                  className="modal fade show d-block"
                  tabIndex="-1"
                  style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          Export Client Utilization
                        </h5>

                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowExportModal(false)}
                        ></button>
                      </div>

                      <div className="modal-body">
                        {/* Month */}
                        <div className="mb-3">
                          <label className="form-label">Month</label>

                          <select
                            className="form-select"
                            value={exportMonth}
                            onChange={(e) =>
                              setExportMonth(Number(e.target.value))
                            }
                          >
                            <option value={1}>January</option>
                            <option value={2}>February</option>
                            <option value={3}>March</option>
                            <option value={4}>April</option>
                            <option value={5}>May</option>
                            <option value={6}>June</option>
                            <option value={7}>July</option>
                            <option value={8}>August</option>
                            <option value={9}>September</option>
                            <option value={10}>October</option>
                            <option value={11}>November</option>
                            <option value={12}>December</option>
                          </select>
                        </div>

                        {/* Year */}
                        <div className="mb-3">
                          <label className="form-label">Year</label>

                          <select
                            className="form-select"
                            value={exportYear}
                            onChange={(e) =>
                              setExportYear(Number(e.target.value))
                            }
                          >
                            {Array.from({ length: 6 }, (_, index) => {
                              const year = moment().year() - index;

                              return (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      <div className="modal-footer">
                        <button
                          className="btn btn-secondary"
                          onClick={() => setShowExportModal(false)}
                        >
                          Cancel
                        </button>

                        <button
                          className="btn btn-success"
                          onClick={handleExport}
                          disabled={loadingExport}
                        >
                          {loadingExport ? "Please wait..." : "Export"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body">
                <DataTable_Component
                  columns={columns}
                  fetchData={fetchData}
                  totalRevenue={totalRevenue}
                  totalTrips={totalTrips}
                  paginationData={{
                    data: apiData,
                    page: pagination.page,
                    limit: pagination.limit,
                    totalClients: pagination.totalClients,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
